import { useEffect, useState } from 'react'
import moment from 'moment'

import {
  addWorksheet,
  createWorkbook,
  currencyFormat,
  DEFAULTNUMHEADERS,
  formatCurrency,
  formatNumber,
  getDefaultSeason,
  numberFormat,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  styleTotalRow,
} from '../../utilities/helpers'
import { getBuyerTypesReport, getSeasons } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'

const yearStart = () => moment().startOf('year').toDate()
const todayEnd = () => moment().endOf('day').toDate()

export default function BuyerTypesReportWrapper({ organization }) {
  const [filterMode, setFilterMode] = useState('season')
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const [fromDate, setFromDate] = useState(yearStart)
  const [toDate, setToDate] = useState(todayEnd)
  const [isLoadingSeasons, setIsLoadingSeasons] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [report, setReport] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false

    const loadSeasons = async () => {
      if (!organization?.uuid) {
        setLoadError('Missing organization context for report.')
        setIsLoadingSeasons(false)
        return
      }

      try {
        const seasonsRes = await getSeasons(organization.uuid)
        if (cancelled) return

        const loadedSeasons = seasonsRes?.data || []
        const defaultSeason = getDefaultSeason(loadedSeasons)
        setSeasons(loadedSeasons)
        setSelectedSeason(
          String(defaultSeason?.id || loadedSeasons[0]?.id || '')
        )
        if (!loadedSeasons.length) setFilterMode('dateRange')
      } catch (error) {
        if (cancelled) return
        console.error('Error loading seasons for Buyer Types Report:', error)
        setFilterMode('dateRange')
      } finally {
        if (!cancelled) setIsLoadingSeasons(false)
      }
    }

    loadSeasons()
    return () => {
      cancelled = true
    }
  }, [organization?.uuid])

  useEffect(() => {
    let cancelled = false

    const loadReport = async () => {
      const validSeasonFilter = filterMode === 'season' && selectedSeason
      const validDateFilter = filterMode === 'dateRange' && fromDate && toDate
      if (!organization?.uuid || (!validSeasonFilter && !validDateFilter))
        return

      setIsLoadingData(true)
      setLoadError('')

      try {
        const res = await getBuyerTypesReport(organization.uuid, {
          seasonId: validSeasonFilter ? selectedSeason : null,
          from: validDateFilter ? fromDate.toISOString() : null,
          to: validDateFilter ? toDate.toISOString() : null,
        })
        if (!cancelled) setReport(res?.data || null)
      } catch (error) {
        if (cancelled) return
        console.error('Error loading Buyer Types Report:', error)
        setLoadError('Unable to load Buyer Types Report.')
        setReport(null)
      } finally {
        if (!cancelled) {
          setIsLoadingData(false)
          completeRefresh()
        }
      }
    }

    loadReport()
    return () => {
      cancelled = true
    }
  }, [
    organization?.uuid,
    filterMode,
    selectedSeason,
    fromDate,
    toDate,
    refreshToken,
  ])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || []
    setFromDate(start || null)
    setToDate(end ? moment(end).endOf('day').toDate() : null)
  }

  const handleExcelExport = () => {
    if (!report?.buyerTypes?.length) return

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Buyer Types', workbook)
    const filterLabel =
      filterMode === 'season'
        ? `Season: ${report?.filter?.season?.name || 'Season'}`
        : `Date range: ${moment(fromDate).format('MMM D, YYYY')} – ${moment(
            toDate
          ).format('MMM D, YYYY')}`

    setHeaders('Buyer Types Report', organization?.name, worksheet, [
      filterLabel,
    ])

    const formatRow = (row) => ({
      buyer_type_name: row.buyerTypeName,
      orders: row.orderCount,
      total_sales: row.totalSales,
      net_sales: row.netSales,
      payments_collected: row.paymentsCollected,
      outstanding: row.outstanding,
    })
    const rows = [
      ...report.buyerTypes,
      { buyerTypeName: 'Total', ...report.totals },
    ]

    setColumnsAndRows(
      Object.keys(formatRow(rows[0])),
      rows,
      formatRow,
      worksheet
    )
    const headerRows = DEFAULTNUMHEADERS + 1
    numberFormat(2, worksheet, headerRows)
    currencyFormat(3, worksheet, headerRows)
    currencyFormat(4, worksheet, headerRows)
    currencyFormat(5, worksheet, headerRows)
    currencyFormat(6, worksheet, headerRows)
    styleTotalRow(worksheet.lastRow, worksheet, false)

    saveWorkbook(workbook, 'buyer types report')
      .then()
      .catch((err) => alert(err.message))
  }

  if (isLoadingSeasons) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  const filterSelected =
    filterMode === 'season'
      ? selectedSeason
      : fromDate && toDate
        ? 'date-range'
        : ''

  return (
    <section className="spacer-sm">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Buyer Types Report</h1>
        </div>
        <p className="section-header-desc">
          Orders, sales, collections, and outstanding balances grouped by buyer
          type for a season or date range.
        </p>
      </header>

      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected={filterSelected}
        fromDate={filterMode === 'dateRange' ? fromDate : null}
        toDate={filterMode === 'dateRange' ? toDate : null}
        onDateRangeChange={
          filterMode === 'dateRange' ? handleDateRangeChange : null
        }
        data={report?.buyerTypes}
        downloadReport={handleExcelExport}
      >
        <Stack>
          <Form.Group controlId="buyer-types-report-filter">
            <Form.Label>Report by</Form.Label>
            <Form.Select
              value={filterMode}
              onChange={(event) => setFilterMode(event.target.value)}
              disabled={isLoadingData}
            >
              <option value="season" disabled={!seasons.length}>
                Season
              </option>
              <option value="dateRange">Date range</option>
            </Form.Select>
          </Form.Group>
        </Stack>
        {filterMode === 'season' ? (
          <SeasonSelector
            seasons={seasons}
            selected={selectedSeason}
            setSelected={setSelectedSeason}
            isLoading={isLoadingData}
          />
        ) : null}
      </ReportActions>

      <Results
        isLoading={isLoadingData}
        report={report}
        loadError={loadError}
      />
    </section>
  )
}

function Results({ isLoading, report, loadError }) {
  if (isLoading) return <LoadingContainer />

  if (loadError) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>{loadError}</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (!report?.buyerTypes?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No orders found for the selected filter</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table className="align-middle">
            <thead>
              <tr className="md">
                <th className="uppercase text-body">Buyer type name</th>
                <th className="text-center uppercase text-body">Orders</th>
                <th className="text-end uppercase text-body">Total sales</th>
                <th className="text-end uppercase text-body">Net sales</th>
                <th className="text-end uppercase text-body">
                  Payments collected
                </th>
                <th className="text-end uppercase text-body">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {report.buyerTypes.map((row) => (
                <tr key={row.buyerTypeId || 'unassigned'} className="md-x">
                  <td>{row.buyerTypeName}</td>
                  <td className="text-center">
                    {formatNumber(row.orderCount)}
                  </td>
                  <td className="text-end">{formatCurrency(row.totalSales)}</td>
                  <td className="text-end">{formatCurrency(row.netSales)}</td>
                  <td className="text-end">
                    {formatCurrency(row.paymentsCollected)}
                  </td>
                  <td className="text-end">
                    {formatCurrency(row.outstanding)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="md-x">
                <td>
                  <strong>Total</strong>
                </td>
                <td className="text-center">
                  <strong>{formatNumber(report.totals?.orderCount)}</strong>
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(report.totals?.totalSales)}</strong>
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(report.totals?.netSales)}</strong>
                </td>
                <td className="text-end">
                  <strong>
                    {formatCurrency(report.totals?.paymentsCollected)}
                  </strong>
                </td>
                <td className="text-end">
                  <strong>{formatCurrency(report.totals?.outstanding)}</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}
